// Remission orders on Canada surtax / safeguard records. A remission is relief
// the importer claims (usually from the Minister, within two years of
// importation) when the transaction meets the order's conditions, so the tool
// can't decide it: the user turns the order on in the Remission Orders table,
// and every surtax the order covers drops out of the duty.

export type RemissionDuty = {
  id?: string | null;
  measureId?: string | null;
  remissionAvailable?: boolean | null;
  remissionOrderName?: string | null;
  remissionOrderNumber?: string | null;
};

// Same shape as the FTA toggles, so the same table renders both.
export type RemissionOption = {
  id: string;
  code: string;
  name: string;
  effect: string;
};

const text = (value: unknown): string => String(value ?? '').trim();

// Keyed by the order rather than the measure: the steel orders, for one, share
// SOR/2025-287 across several surtax records, and claiming the order claims it
// for all of them. Falls back to the measure only when the record names no
// order at all.
export const remissionKey = (duty: RemissionDuty): string | null => {
  if (duty.remissionAvailable !== true) {
    return null;
  }
  return (
    text(duty.remissionOrderNumber) ||
    text(duty.remissionOrderName) ||
    text(duty.id) ||
    text(duty.measureId) ||
    null
  );
};

export const remissionOptions = (
  duties: readonly RemissionDuty[] | null | undefined,
): RemissionOption[] => {
  const options: RemissionOption[] = [];
  (duties ?? []).forEach((duty) => {
    const key = remissionKey(duty);
    if (key === null || options.some((option) => option.id === key)) {
      return;
    }
    options.push({
      id: key,
      code: text(duty.remissionOrderNumber),
      name: text(duty.remissionOrderName) || 'Remission order',
      effect: 'Removes surtax',
    });
  });
  return options;
};

export const isRemitted = (
  duty: RemissionDuty,
  enabled: Record<string, boolean>,
): boolean => {
  const key = remissionKey(duty);
  return key !== null && Boolean(enabled[key]);
};
